import { useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { scadenze as apiScadenze } from '../api/scadenziario.ts';
import type { Scadenza } from '../api/tipi-dati.ts';
import { Dialogo, RiepilogoScadenza } from './Dialogo.tsx';
import { dataBreve, oggi } from './formato.ts';
import { descriviPeriodicita, proponiRinnovo } from './rinnovo.ts';

/**
 * Dialogo di completamento.
 *
 * È l'unico punto dell'applicazione che **crea un record che l'utente non ha
 * chiesto**: completando una scadenza ricorrente ne nasce una futura. Se non
 * fosse dichiarato con chiarezza, mesi dopo qualcuno troverebbe nell'elenco
 * scadenze che non ricorda di aver inserito.
 *
 * Per questo la data proposta, il calcolo da cui esce e il riepilogo finale
 * di "cosa verrà salvato" sono in evidenza, non in fondo in caratteri piccoli.
 */
export function DialogoCompletamento({
  scadenza,
  onChiudi,
  onCompletata,
}: {
  scadenza: Scadenza;
  onChiudi: () => void;
  onCompletata: (messaggio: string) => void;
}) {
  const [dataCompletamento, setDataCompletamento] = useState(oggi());
  const [generaRinnovo, setGeneraRinnovo] = useState(scadenza.periodicitaMesi !== null);
  const [dataRinnovo, setDataRinnovo] = useState(
    () => proponiRinnovo(oggi(), scadenza.periodicitaMesi) ?? '',
  );
  const [rinnovoToccato, setRinnovoToccato] = useState(false);
  const [note, setNote] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const ricorrente = scadenza.periodicitaMesi !== null;
  const nelFuturo = dataCompletamento > oggi();

  /**
   * Cambiando la data di esecuzione si ricalcola quella del rinnovo — ma solo
   * se l'utente non l'ha già corretta a mano: sovrascrivere una scelta
   * esplicita è il modo più rapido per far perdere fiducia in un modulo.
   */
  function cambiaDataCompletamento(nuova: string) {
    setDataCompletamento(nuova);
    if (!rinnovoToccato && nuova !== '') {
      setDataRinnovo(proponiRinnovo(nuova, scadenza.periodicitaMesi) ?? '');
    }
  }

  async function conferma() {
    setErrore(null);
    setInCorso(true);

    try {
      const esito = await apiScadenze.completa(scadenza.id, {
        dataCompletamento,
        ...(generaRinnovo && dataRinnovo !== ''
          ? { dataProssimaScadenza: dataRinnovo }
          : { senzaRinnovo: true }),
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      });

      onCompletata(
        esito.rinnovo === null
          ? `«${scadenza.tipo.nome}» completata. Nessun rinnovo generato.`
          : `«${scadenza.tipo.nome}» completata. Creata la prossima scadenza per il ${dataBreve(esito.rinnovo.dataScadenza)}.`,
      );
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo="Segna come completata" aperto onChiudi={onChiudi}>
      <RiepilogoScadenza
        soggetto={scadenza.soggetto.denominazione}
        soggettoEliminato={scadenza.soggetto.eliminato}
        codice={scadenza.soggetto.codice}
        reparto={scadenza.soggetto.reparto}
        tipo={scadenza.tipo.nome}
        tipoEliminato={scadenza.tipo.eliminato}
        dataPrevista={dataBreve(scadenza.dataScadenza)}
        giorni={scadenza.giorniMancanti}
      />

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className="campo">
        <label htmlFor="data-fatta">Data in cui è stata fatta</label>
        <div className="campo-affiancato">
          <input
            id="data-fatta"
            type="date"
            value={dataCompletamento}
            max={oggi()}
            onChange={(e) => cambiaDataCompletamento(e.target.value)}
          />
          <span className="aiuto">predefinita a oggi, modificabile</span>
        </div>
        {nelFuturo && (
          <p className="aiuto errore" role="alert">
            La data non può essere nel futuro.
          </p>
        )}
      </div>

      {ricorrente ? (
        <fieldset className="blocco-rinnovo">
          <legend>
            <span aria-hidden="true">⟳</span> Rinnovo automatico
          </legend>

          <label className="interruttore">
            <input
              type="checkbox"
              checked={generaRinnovo}
              onChange={(e) => setGeneraRinnovo(e.target.checked)}
            />
            Genera il rinnovo
          </label>

          {generaRinnovo && (
            <>
              <p className="rinnovo-testo">
                Alla conferma verrà creata una nuova scadenza <strong>PIANIFICATA</strong> per:
              </p>
              <div className="campo-affiancato">
                <input
                  type="date"
                  aria-label="Data della prossima scadenza"
                  value={dataRinnovo}
                  onChange={(e) => { setDataRinnovo(e.target.value); setRinnovoToccato(true); }}
                />
                <span className="aiuto">
                  preavviso {scadenza.preavvisoGiorni} giorni (dal tipo)
                </span>
              </div>
              <p className="aiuto">
                Calcolata come data in cui è stata fatta ({dataBreve(dataCompletamento)}) +
                periodicità di questa scadenza ({descriviPeriodicita(scadenza.periodicitaMesi)}).
                Modificala se il certificato riporta una validità propria.
              </p>
            </>
          )}
        </fieldset>
      ) : (
        <p className="aiuto blocco-nota">
          Il tipo «{scadenza.tipo.nome}» è <strong>una tantum</strong>: non verrà creata
          nessuna scadenza successiva.
        </p>
      )}

      <div className="campo">
        <label htmlFor="note-completamento">Note <span className="tenue">facoltative</span></label>
        <textarea
          id="note-completamento"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Esito, ente, numero certificato…"
        />
      </div>

      <div className="dialogo-azioni">
        <button
          type="button"
          className="bottone bottone-principale"
          onClick={() => void conferma()}
          disabled={inCorso || nelFuturo || dataCompletamento === ''}
        >
          {inCorso ? 'Salvataggio…' : 'Conferma completamento'}
        </button>
        <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
          Annulla
        </button>

        {/* Dire esplicitamente quante cose si stanno salvando: è l'avviso che
            evita la sorpresa di trovare più tardi una scadenza mai inserita. */}
        <p className="aiuto azioni-nota">
          {ricorrente && generaRinnovo && dataRinnovo !== ''
            ? `Verranno salvate due cose: il completamento e la scadenza del ${dataBreve(dataRinnovo)}.`
            : 'Verrà salvato solo il completamento.'}
        </p>
      </div>
    </Dialogo>
  );
}
